import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";

let eventSource = null;
let reconnectTimeout = null;

/**
 * Resuelve una URL o ruta relativa a una URL completa y válida hacia la API del backend
 * @param {String} urlOrPath Ruta relativa (ej: '/api/export/download/123' o 'export/download/123') o URL completa
 * @returns {String} URL absoluta hacia el backend
 */
export function getFullDownloadUrl(urlOrPath) {
  if (!urlOrPath) return "";

  let cleanPath = urlOrPath;
  // Detectar y limpiar URLs absolutas heredadas (ej. /storage/Keywords.xlsx)
  if (urlOrPath.startsWith("http://") || urlOrPath.startsWith("https://")) {
    try {
      const parsed = new URL(urlOrPath);
      if (
        parsed.hostname.includes("smsconsulting") ||
        parsed.pathname.includes("/storage/") ||
        parsed.pathname.includes("/export/") ||
        parsed.pathname.includes("/api/")
      ) {
        cleanPath = parsed.pathname + parsed.search;
      } else {
        return urlOrPath;
      }
    } catch {
      return urlOrPath;
    }
  }

  let apiUrl = process.env.VUE_APP_API_URL || "http://localhost:5000/api/";
  if (!apiUrl.endsWith("/")) {
    apiUrl += "/";
  }

  // Quitar barras iniciales
  let clean = cleanPath.replace(/^\/+/, "");

  // Si apiUrl ya termina con /api/ y la ruta empieza con api/, evitar duplicar (/api/api/...)
  if (apiUrl.endsWith("/api/") && clean.startsWith("api/")) {
    clean = clean.replace(/^api\/+/, "");
  }

  return apiUrl + clean;
}

/**
 * Descarga un archivo de forma asíncrona mediante Axios (con token Bearer) y dispara la descarga en el navegador
 * @param {String} urlOrPath URL relativa o absoluta
 * @param {String} defaultFilename Nombre del archivo por defecto
 */
export async function triggerDownload(urlOrPath, defaultFilename = "Exportacion.xlsx") {
  const fullUrl = getFullDownloadUrl(urlOrPath);
  const token = localStorage.getItem("authToken");

  try {
    const response = await axios.get(fullUrl, {
      responseType: "blob",
      skipLoading: true,
      headers: {
        "X-Skip-Loading": "true"
      }
    });

    let filename = defaultFilename;
    const disposition = response.headers && (response.headers["content-disposition"] || response.headers["Content-Disposition"]);
    if (disposition) {
      const match = disposition.match(/filename="?([^";]+)"?/);
      if (match && match[1]) {
        filename = match[1];
      }
    }

    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.style.display = "none";
    link.href = blobUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }, 300);
  } catch (error) {
    console.error("[triggerDownload] Error al descargar con axios:", error);
    // Fallback: abrir en nueva ventana pasando el token en el query param
    const authUrl = token
      ? `${fullUrl}${fullUrl.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}`
      : fullUrl;
    window.open(authUrl, "_blank");
  }
}

/**
 * Inicia la conexión SSE (Server-Sent Events) / Comet con el backend
 * @param {String} token Token de autenticación JWT
 */
export function initNotificationStream(token) {
  const authToken = token || localStorage.getItem("authToken");
  if (!authToken) return;

  // Si ya hay una conexión abierta, cerrarla antes de crear una nueva
  closeNotificationStream();

  const streamUrl = getFullDownloadUrl(`notifications/stream?token=${encodeURIComponent(authToken)}`);

  try {
    eventSource = new EventSource(streamUrl);

    eventSource.addEventListener("connected", (event) => {
      console.log("[SSE Comet] Conectado exitosamente al canal de notificaciones:", event.data);
    });

    // Evento cuando una exportación o tarea bulk finaliza con éxito
    eventSource.addEventListener("export_completed", (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log("[SSE Comet] Evento export_completed recibido:", data);
        handleExportCompleted(data);
      } catch (err) {
        console.error("[SSE Comet] Error parseando export_completed:", err);
      }
    });

    // Evento cuando una exportación o tarea bulk falla
    eventSource.addEventListener("export_failed", (event) => {
      try {
        const data = JSON.parse(event.data);
        console.error("[SSE Comet] Evento export_failed recibido:", data);
        handleExportFailed(data);
      } catch (err) {
        console.error("[SSE Comet] Error parseando export_failed:", err);
      }
    });

    eventSource.onerror = (err) => {
      console.warn("[SSE Comet] Error en conexión de eventos. Reintentando en 10s...", err);
      closeNotificationStream();
      // Reintento automático de conexión tras 10 segundos si el usuario sigue autenticado
      if (localStorage.getItem("authToken")) {
        reconnectTimeout = setTimeout(() => {
          initNotificationStream();
        }, 10000);
      }
    };
  } catch (e) {
    console.error("[SSE Comet] Error inicializando EventSource:", e);
  }
}

/**
 * Cierra la conexión SSE activa
 */
export function closeNotificationStream() {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
  if (eventSource) {
    eventSource.close();
    eventSource = null;
    console.log("[SSE Comet] Conexión cerrada");
  }
}

/**
 * Maneja la alerta interactiva cuando la exportación termina
 */
function handleExportCompleted(data) {
  const downloadTarget = data.download_url || `export/download/${data.task_id}`;
  const rowCountText = data.total_rows !== undefined ? `<br><small class="text-muted">Total de registros: <b>${data.total_rows}</b></small>` : "";
  const messageText = data.message ? `<br><small class="text-info">${data.message}</small>` : "";

  // Notificar a componentes abiertos (ej. Tareas Bulk para autorefresco)
  window.dispatchEvent(new CustomEvent("genesis-task-completed", { detail: data }));

  Swal.fire({
    title: "¡Exportación Finalizada!",
    html: `
      <div style="font-size: 15px; margin-top: 10px;">
        El archivo <b>${data.filename || "exportación"}</b> ya está disponible para descargar.
        ${rowCountText}
        ${messageText}
      </div>
    `,
    icon: "success",
    showCancelButton: true,
    confirmButtonText: '<i class="bi bi-download"></i> Descargar archivo',
    cancelButtonText: "Cerrar",
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#6c757d",
    allowOutsideClick: true,
  }).then((result) => {
    if (result.isConfirmed) {
      triggerDownload(downloadTarget, data.filename || "Exportacion.xlsx");
    }
  });
}

/**
 * Maneja la alerta de error si la tarea falla
 */
function handleExportFailed(data) {
  // Notificar a componentes abiertos
  window.dispatchEvent(new CustomEvent("genesis-task-failed", { detail: data }));

  Swal.fire({
    title: "Error en la Exportación",
    text: `No se pudo completar la exportación de ${data.title || "datos"}: ${data.error || "Error desconocido"}`,
    icon: "error",
    confirmButtonText: "Aceptar",
    confirmButtonColor: "#d33",
  });
}
